<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import {
  mdiAccessPointNetwork,
  mdiLogout,
  mdiThemeLightDark,
  mdiTranslate,
  mdiViewDashboardOutline,
} from '@mdi/js';
import { LOCALES, setLocale, type Locale } from './plugins/i18n.ts';
import { THEME_CHOICES, useThemePreference } from './composables/themePreference.ts';
import { useDocumentTitle } from './composables/pageTitle.ts';
import { useApi, useSession } from './api/client.ts';
import { useEstateStore } from './stores/estate.ts';
import TwinTree from './components/TwinTree.vue';

/**
 * The frame every page sits in: app bar, navigation, content.
 *
 * The drawer is permanent on a wide screen and temporary on a narrow one,
 * which Vuetify decides from the breakpoint. A control-room wall display and a
 * technician's phone are both real users of the same dashboard.
 */
const { t, locale } = useI18n();
const { mdAndUp } = useDisplay();
const drawer = ref<boolean | null>(null);
const { choice: theme, apply: applyTheme } = useThemePreference();
useDocumentTitle();

const session = useSession();

// The estate is loaded once, for the whole session: the tree, and the silence
// cutoff every status colour depends on.
void useEstateStore().load(useApi());
const initials = session.user.name
  .split(/\s+/)
  .map((part) => part[0] ?? '')
  .join('')
  .slice(0, 2)
  .toUpperCase();

const nav = [
  { to: { name: 'overview' }, icon: mdiViewDashboardOutline, label: 'nav.overview' },
  { to: { name: 'devices' }, icon: mdiAccessPointNetwork, label: 'nav.devices' },
] as const;
</script>

<template>
  <v-app>
    <a class="skip-link" href="#main">{{ t('app.skipToContent') }}</a>

    <v-app-bar flat border="b" density="comfortable">
      <v-app-bar-nav-icon v-if="!mdAndUp" :aria-label="t('nav.menu')" @click="drawer = !drawer" />
      <router-link :to="{ name: 'overview' }" class="brand d-flex align-center ml-3">
        <img src="/favicon.svg" alt="" width="28" height="28" />
        <span class="ml-2 text-title-large font-weight-bold">{{ t('app.name') }}</span>
        <span class="ml-3 text-body-medium text-medium-emphasis d-none d-sm-inline">
          {{ t('app.tagline') }}
        </span>
      </router-link>

      <v-spacer />

      <v-menu>
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            :prepend-icon="mdiTranslate"
            :aria-label="`${t('settings.language')}: ${locale.toUpperCase()}`"
          >
            {{ locale.toUpperCase() }}
          </v-btn>
        </template>
        <v-list density="compact" :aria-label="t('settings.language')">
          <v-list-item
            v-for="code in LOCALES"
            :key="code"
            :active="locale === code"
            :lang="code"
            :title="code === 'de' ? 'Deutsch' : 'English'"
            @click="setLocale(code as Locale)"
          />
        </v-list>
      </v-menu>

      <v-menu>
        <template #activator="{ props }">
          <v-btn v-bind="props" :icon="mdiThemeLightDark" :aria-label="t('settings.theme')" />
        </template>
        <v-list density="compact" :aria-label="t('settings.theme')">
          <v-list-item
            v-for="option in THEME_CHOICES"
            :key="option"
            :active="theme === option"
            :title="t(`settings.themes.${option}`)"
            @click="applyTheme(option)"
          />
        </v-list>
      </v-menu>

      <v-menu>
        <template #activator="{ props }">
          <v-btn v-bind="props" icon :aria-label="`${t('auth.account')}: ${session.user.name}`">
            <v-avatar color="primary" size="32">
              <span class="text-label-medium font-weight-bold">{{ initials }}</span>
            </v-avatar>
          </v-btn>
        </template>
        <v-list density="compact" min-width="220">
          <v-list-item :title="session.user.name" :subtitle="session.user.username" />
          <v-divider />
          <v-list-item
            :prepend-icon="mdiLogout"
            :title="t('auth.logout')"
            @click="session.logout()"
          />
        </v-list>
      </v-menu>
    </v-app-bar>

    <v-navigation-drawer v-model="drawer" :permanent="mdAndUp" width="300">
      <v-list nav density="comfortable">
        <v-list-item
          v-for="item in nav"
          :key="item.label"
          :to="item.to"
          :prepend-icon="item.icon"
          :title="t(item.label)"
          exact
        />
      </v-list>
      <v-divider class="my-2" />
      <v-list nav density="compact">
        <v-list-subheader>{{ t('nav.sites') }}</v-list-subheader>
      </v-list>
      <TwinTree />
    </v-navigation-drawer>

    <v-main id="main" tabindex="-1">
      <router-view />
    </v-main>
  </v-app>
</template>

<style scoped>
.brand {
  color: inherit;
  text-decoration: none;
}
</style>
